import { getLocalizedString } from "common/components/localization";
import type { AppFeatures, AppKind } from "common/components/SerializedApp";
import { getAppKindFromFeatures } from "common/components/SerializedApp";
import type { EminenceFlags } from "common/Database/eminence";
import type { OnClickHandler } from "hooks/use-on-click-from-action";
import { getRenderMode } from "lib/browser-hacks";
import * as React from "react";
import { CellMeasurer, CellMeasurerCache } from "react-virtualized/dist/es/CellMeasurer";
import type { ListRowProps } from "react-virtualized/dist/es/List";
import { List as VList } from "react-virtualized/dist/es/List";
import { TailwindThemeProvider } from "sharedUI/lib/tailwind-theme-provider";
import { mergeTheme } from "sharedUI/lib/theme-utils";
import type { WindowScrollerChildProps } from "sharedUI/lib/window-scroller";
import { WindowScroller } from "sharedUI/lib/window-scroller";
import { ScaleRenderContext } from "sharedUI/ui/views/smartphone/smartphone";
import { withTheme } from "styled-components";
import { TabletMaxListWidth } from "webapp/lib/theme/tablet-overlay";
import type { RuntimeTheme } from "../../../lib/theme/theme";
import { listOverlay } from "../../../lib/theme/theme-overlays/list-overlay";
import { css, styled } from "../../../styled-components";
import EmptyWrapper from "../../views/empty-wrapper/empty-wrapper";
import { BigLabel, BigLabelContainer } from "../../views/labels/labels";
import SeeAllButton from "../../views/see-all-button/see-all-button";
import { isGroupHeader, ItemRowIndexContextProvider, ListCacheClearingContextProvider, ListProxy } from "./list-lib";
import { GroupHeader } from "./list-style";
import type { ListGroup } from "./list-types";

export function getScrollParent(node: HTMLElement | null): HTMLElement | undefined {
    if (node === null) {
        return undefined;
    }

    const computedStyle = window.getComputedStyle(node);

    if (node.id.startsWith("screenScrollView") || computedStyle.overflowY === "auto") {
        return node;
    } else {
        return getScrollParent(node.parentElement);
    }
}

interface Props<T> extends BaseProps<T> {
    readonly theme: RuntimeTheme;
}

interface BaseProps<T> {
    readonly items?: readonly T[];
    readonly groups?: readonly ListGroup<T>[];
    readonly children: (item: T, index: number | string, columnWidth: number | undefined) => React.ReactNode;
    readonly hasUnevenRows: boolean;
    readonly topAlign: boolean;
    readonly forceUpdate?: boolean;
    readonly title: string;
    readonly style?: React.CSSProperties;
    readonly emptyMessage?: string;
    readonly onSeeAllClick?: OnClickHandler;
    readonly appFeatures: AppFeatures;
    readonly eminenceFlags: EminenceFlags;
}

interface State {
    refWidth: number | undefined;
    scrollElement?: Element;
}

const Wrapper = styled.div<{ topAlign: boolean; hasTitle: boolean }>`
    ${p => {
        if (!p.topAlign) {
            return css`
                padding-top: ${p.theme.componentSmallInnerVerticalPadding}px;

                *:not(.highlight-view) &:first-child,
                .highlight-view:first-child & {
                    padding-top: ${Math.max(
                        p.theme.list.topMargin,
                        p.hasTitle ? p.theme.componentSmallInnerVerticalPadding : 1
                    )}px;
                }
            `;
        }
        return css`
            padding-top: ${p.hasTitle ? p.theme.componentSmallInnerVerticalPadding : 1}px;
            *:not(.highlight-view) &:not(:first-child),
            .highlight-view:not(:first-child) & {
                padding-top: ${p.theme.componentSmallInnerVerticalPadding}px;
            }
        `;
    }}

    padding-bottom: ${p => p.theme.componentSmallInnerVerticalPadding}px;
`;

class ListInner<T> extends React.Component<Props<T>, State> {
    public state: State = {
        refWidth: undefined,
    };

    private privateRef = React.createRef<HTMLDivElement>();
    private windowScroller = React.createRef<any>();
    private vlist = React.createRef<VList>();
    private renderWidth = 0;
    private updateInterval: ReturnType<typeof setInterval> | undefined;

    private cache: CellMeasurerCache;

    private timeoutID: NodeJS.Timeout | undefined = undefined;
    // Track which row indexes need to be cleared from the cache when clean up happens.
    // See `itemSizeMightHaveChanged`.
    private dirtyRowIndexes: Set<number> = new Set();

    constructor(props: Props<T>) {
        super(props);

        this.cache = this.createCache();
    }

    private get appKind(): AppKind {
        return getAppKindFromFeatures(this.props.appFeatures);
    }

    private get listItems(): ListProxy<T> {
        return new ListProxy(this.props.items, this.props.groups);
    }

    private rowHeight = (p: { index: number }) => {
        return this.cache.rowHeight(p);
    };

    private createCache() {
        return new CellMeasurerCache({
            defaultHeight: 62,
            fixedWidth: true,
            keyMapper: (rowIndex: number) => {
                if (this.props.hasUnevenRows) return rowIndex;
                if (this.props.groups !== undefined && isGroupHeader(this.listItems.get(rowIndex))) {
                    return 2;
                }

                return 1;
            },
        });
    }

    private get ref() {
        return this.privateRef;
    }

    private renderRow = (props: ListRowProps): React.ReactNode => {
        const { index, key, parent, style } = props;
        const item = this.listItems.get(index);
        if (isGroupHeader(item)) {
            const onSeeAllClick = item.onSeeAll;
            return (
                <CellMeasurer key={key} cache={this.cache} parent={parent} columnIndex={0} rowIndex={index}>
                    <div style={style} role="row">
                        <div role="cell">
                            <GroupHeader>
                                <BigLabel>{item.groupHeaderName}</BigLabel>
                                {onSeeAllClick !== undefined && (
                                    <SeeAllButton onClick={onSeeAllClick}>
                                        {getLocalizedString("seeAll", this.appKind)}
                                    </SeeAllButton>
                                )}
                            </GroupHeader>
                        </div>
                    </div>
                </CellMeasurer>
            );
        } else {
            return (
                <CellMeasurer key={key} cache={this.cache} parent={parent} columnIndex={0} rowIndex={index}>
                    <div style={style} role="row">
                        <ItemRowIndexContextProvider value={index}>
                            <div role="cell">{this.props.children(item, key, this.renderWidth)}</div>
                        </ItemRowIndexContextProvider>
                    </div>
                </CellMeasurer>
            );
        }
    };

    public componentDidMount() {
        if (this.ref.current === null) return;

        this.setState({ scrollElement: getScrollParent(this.ref.current) });

        if (this.updateInterval !== undefined) {
            clearInterval(this.updateInterval);
        }

        this.updateInterval = setInterval(() => {
            if (this.windowScroller.current) {
                this.windowScroller.current.updatePosition();
            }
        }, 1000);
    }

    public componentDidUpdate() {
        if (this.state.refWidth !== this.ref.current?.clientWidth) {
            this.setState({ refWidth: this.ref.current?.clientWidth });
        }

        if (this.props.forceUpdate === true && this.vlist.current !== null) {
            this.cache.clearAll();
            this.vlist.current.forceUpdateGrid();
            return;
        }

        // If rows are uneven (because of text wrapping), or if we have title rows, we can't predict row heights.
        // The easy way around this is to invalidate the whole cache and recalculate heights.
        if (this.props.groups !== undefined || this.props.hasUnevenRows) {
            this.cache.clearAll();
            return;
        }
    }

    public componentWillUnmount() {
        if (this.updateInterval !== undefined) {
            clearInterval(this.updateInterval);
            this.updateInterval = undefined;
        }
    }

    private onSeeAllClick = async () => {
        if (this.props.onSeeAllClick !== undefined) {
            await this.props.onSeeAllClick(false);
        }
    };

    private itemSizeMightHaveChanged = (rowIndex: number) => {
        if (this.timeoutID !== undefined) {
            clearTimeout(this.timeoutID);
        }

        this.dirtyRowIndexes.add(rowIndex);

        this.timeoutID = setTimeout(() => {
            for (const idx of this.dirtyRowIndexes) {
                this.cache.clear(idx, 0);
            }
            this.dirtyRowIndexes.clear();

            this.vlist.current?.forceUpdateGrid();
        }, 0);
    };

    public render(): React.ReactNode {
        const { theme, style, emptyMessage, onSeeAllClick } = this.props;

        if (this.state.scrollElement === undefined) return <div ref={this.ref} />;

        if (this.listItems.length === 0) {
            if (emptyMessage === undefined) return null;
            return <EmptyWrapper targetRef={this.ref}>{emptyMessage}</EmptyWrapper>;
        }

        const newTheme = mergeTheme(theme, [listOverlay]);

        const hasTitle = this.props.title !== "" || onSeeAllClick !== undefined;

        return (
            <TailwindThemeProvider theme={newTheme}>
                <ScaleRenderContext.Consumer>
                    {scale => (
                        <Wrapper
                            data-test="app-vertical-list"
                            hasTitle={hasTitle}
                            topAlign={this.props.topAlign}
                            ref={this.ref}
                            style={style}>
                            {hasTitle && (
                                <BigLabelContainer>
                                    <BigLabel>{this.props.title}</BigLabel>
                                    {onSeeAllClick !== undefined && (
                                        <SeeAllButton onClick={this.onSeeAllClick}>
                                            {getLocalizedString("seeAll", this.appKind)}
                                        </SeeAllButton>
                                    )}
                                </BigLabelContainer>
                            )}
                            <WindowScroller
                                ref={this.windowScroller}
                                scrollElement={this.state.scrollElement}
                                scale={scale}>
                                {(props: WindowScrollerChildProps) => {
                                    let width = this.ref.current?.clientWidth;
                                    const height = props.height;
                                    if (!width || !height) {
                                        return <div />;
                                    }

                                    if (getRenderMode(this.props.appFeatures, this.props.eminenceFlags) === "tablet") {
                                        width = Math.min(TabletMaxListWidth, width);
                                    }
                                    if (this.renderWidth !== width) {
                                        this.cache.clearAll();
                                    }

                                    this.renderWidth = width;
                                    return (
                                        <div ref={props.registerChild as any}>
                                            <ListCacheClearingContextProvider value={this.itemSizeMightHaveChanged}>
                                                <VList
                                                    key={this.listItems.length}
                                                    ref={this.vlist}
                                                    autoHeight={true}
                                                    width={width}
                                                    height={height}
                                                    isScrolling={props.isScrolling}
                                                    scrollTop={props.scrollTop}
                                                    estimatedRowSize={this.props.hasUnevenRows ? 500 : 300}
                                                    deferredMeasurementCache={this.cache}
                                                    rowHeight={this.rowHeight}
                                                    rowCount={this.listItems.length}
                                                    rowRenderer={this.renderRow}
                                                    overscanRowCount={1}
                                                />
                                            </ListCacheClearingContextProvider>
                                        </div>
                                    );
                                }}
                            </WindowScroller>
                        </Wrapper>
                    )}
                </ScaleRenderContext.Consumer>
            </TailwindThemeProvider>
        );
    }
}

const HighlightedList = withTheme(ListInner);

function List<T>(props: BaseProps<T>) {
    const TypedList = HighlightedList as React.ComponentType<BaseProps<T>>;
    return <TypedList {...props} />;
}

export default List;
